<!--
	Copyright (c) 2021-present, Bäumer, Afonso, Meissner GmbH
	All rights reserved.
-->
<script setup>
import { templateRef, useMediaControls } from "@vueuse/core";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { computed, onMounted, onUnmounted, ref, watch } from "vue";

const props = defineProps({
    id: {
        type: [String, Number],
    },

    src: {
        type: String,
        required: true,
    },

    tracks: Array,

    controls: {
        type: Boolean,
        default: true,
    },
    scrollTrigger: Boolean,
    autoplay: Boolean,
    loop: Boolean,
    muted: Boolean,
    playsinline: Boolean,
});

const aspectRatio = ref(16 / 9);
const video = ref();

const videoAttrs = computed(() => {
    return {
        controls: props.controls,
        autoplay: props.autoplay,
        loop: props.loop,
        muted: props.muted,
        playsinline: props.playsinline,
        preload: "metadata",
        fetchpriority: props.autoplay ? "high" : "auto",
        ["data-scroll-trigger"]: props.scrollTrigger
    };
});

const { playing, muted, currentTime, duration, ended } = useMediaControls(
    video,
    {
        tracks: props.tracks,
    }
);

watch(
    () => playing.value,
    (newValue) => {
        const videoClasses = video.value.closest(".section").classList;
        if (newValue && !videoClasses.contains("video-playing")) {
            videoClasses.add("video-playing");
        } else {
            videoClasses.remove("video-playing");
        }
    }
);

const getAspectRatio = ref(null);

watch(
    () => aspectRatio.value,
    (newValue) => {
        getAspectRatio.value =
            aspectRatio.value < 1
                ? "portrait"
                : aspectRatio.value > 1
                ? "landscape"
                : "square";
    },
    { immediate: true }
);

watch(
    () => props.muted,
    (newValue) => {
        muted.value = newValue;
    },
    { immediate: true }
);

// const scrollTrigger = ref()
const videoPlayerRef = ref()

onMounted(async () => {
    const videoMetaDataLoaded = new Promise((resolve, reject) => {
        video.value.addEventListener("loadedmetadata", resolve, { once: true } );
    })

    await videoMetaDataLoaded;

    aspectRatio.value =
        video.value.videoWidth / video.value.videoHeight;

});
</script>

<template>
    <div
        ref="videoPlayerRef"
        class="video-player"
        :class="{ [`video-player--aspect-${getAspectRatio}`]: true }"
    >
        <div class="video-player__content">
            <video
                ref="video"
                class="video-player__video"
                :src="src"
                v-bind="videoAttrs"
            />

            <div
                v-if="!playing && currentTime == 0"
                class="video-player__poster"
                @click="playing = true"
            >
                <slot name="poster" />

                <div v-if="!autoplay" class="video-player__play-button">
                    <slot name="icon" />
                </div>
            </div>
        </div>
    </div>
</template>

<style lang="scss">
@use "@/scss/framework" as *;

@layer components.video-player {

    :root {
        --video-player-font-poster: normal #{clamped(12px, 60px)} / 1 var(--font-family-body);
        --video-player-play-icon-color: var(--color-white);
        --video-player-play-icon-color--hover: var(--color-white);
    }

    .video-player {
        --poster-darken-color: hsla(var(--color-dark-hsl), 0.5);

        &__content {
            position: relative;
            // NOTE: aspect-ratio misbehaves in safari
            padding-bottom: calc(100% / v-bind(aspectRatio));

            overflow: hidden;
        }

        &__video {
            position: absolute;
            top: 0;
            left: 0;
            z-index: 1;
            width: 100%;
            height: 100%;
            object-fit: contain;;
        }

        video::-webkit-media-controls-overlay-play-button {
            display: none !important;
        }

        &__poster {
            position: absolute;
            top: 0;
            left: 0;
            z-index: 2;
            width: 100%;
            height: 100%;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;

            // safari fix
            picture {
                width: 100%;
            }

            .picture-component {
                flex: 1;
            }

            @include hover {
                .video-player__play-button {
                    transform: scale(1.3);
                    color: var(--video-player-play-icon-color--hover);
                }
            }

            &--darken {
                background-color: var(--poster-darken-color);
            }
        }

        &__poster-text {
            position: absolute;
            align-self: end;
            inset: var(--size-20);
            font: var(--video-player-font-poster);
            color: var(--color-white);

            display: grid;
            align-items: end;
        }

        &__play-button {
            position: absolute;
            color: var(--video-player-play-icon-color);
            transition:
                transform var(--easing-back-in-out) var(--duration-default),
                color var(--easing-back-in-out) var(--duration-default);

            svg {
                @include svg-auto-color;
                width: #{clamped(42px, 130px)};
                height: #{clamped(42px, 130px)};
            }
        }

        &--aspect-portrait {
            .video-player__content {
                @include mq-from(sm) {
                    aspect-ratio: 16/9 !important;
                }

                video {
                    @include mq-from(md) {
                        width: 100% !important;
                        height: 100% !important;
                        object-fit: contain !important;
                    }
                }
            }
        }
    }
}
</style>
